import { api } from '@/lib/api-client';
import { readCsrfCookie } from '@/lib/storage';

/**
 * Stable user contract — mirrors apps/api .../request-user.ts. Treat as
 * a frozen interface across Keycloak swap (ports designed to keep this
 * shape identical pre/post-migration).
 */
export interface RequestUser {
  sub: string;
  tenantId: string | null;
  role: 'owner' | 'manager' | 'cashier' | 'admin';
  email: string | null;
  name: string;
  status: 'active';
  type: 'user' | 'admin';
}

/**
 * /login + /refresh response. The refresh token is set as an HttpOnly
 * cookie on the response and is NEVER in the body — JS doesn't see it,
 * so XSS can't exfiltrate it.
 */
export interface SessionResponse {
  accessToken: string;
  expiresInSeconds: number;
}

export const authApi = {
  login: (email: string, password: string): Promise<SessionResponse> =>
    api.post('/v1/auth/login', { email, password }, { skipAuth: true }),

  me: (): Promise<RequestUser> => api.get('/v1/auth/me'),

  /**
   * Server reads the refresh from the cookie and clears the cookie pair
   * regardless. Echoes the CSRF header so the server accepts the revoke.
   * Idempotent on the server side — calling logout with an already-
   * dead session is a 204, never an error.
   */
  logout: (): Promise<void> => {
    const csrf = readCsrfCookie();
    return api.post('/v1/auth/logout', undefined, {
      skipAuth: true,
      headers: csrf ? { 'x-csrf-token': csrf } : {},
    });
  },
};
